import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import { download, tone } from "./hctc_common";
import "./hctc.css";

// Excel import of historical cases (screen hist_import). The template's columns follow the case filing form.
// Step 1 validates (nothing saved), step 2 imports the rows that passed. Duplicates are skipped, never overwritten.
export default function HistoricalImport() {
  const { token, setIsExpired } = useContext(ShareContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");

  const onTemplate = async () => {
    setErr("");
    setLoading(true);
    try {
      await download(token, setIsExpired, "historical/import-template", "historical-cases-template.xlsx");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const send = async (validateOnly) => {
    if (!file) return;
    setErr("");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${Constants.apiUrl}/historical/import?validateOnly=${validateOnly}`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await resp.json();
      if (d.code === 0) setRes(d);
      else if (d.code === -10) setIsExpired(true);
      else setErr(d.status || "Import failed");
    } catch (e) {
      console.error(e);
      setErr("Import failed. Check the file and try again.");
    }
    setLoading(false);
  };

  const rows = (res?.results || []).filter((r) => filter === "all" || r.result === filter);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <JisButton variant="plain" onClick={() => navigate("/historical-cases")}>
            Back
          </JisButton>
          <div>
            <h2>Historical Case Import</h2>
            <div className="hctc-sub">Imported records start as Pending Verification. The original case numbers are kept; duplicates are reported, not imported.</div>
          </div>
        </div>
      </div>

      <div className="hctc-card">
        <div className="hctc-section-title">1. Get the template</div>
        <p style={{ fontSize: 13.5 }}>
          The columns follow the case filing form: case number(s), title, filing date, court, division, case type, category, sub category, origin, judge, registrar,
          file and archive references, record source, claimants, defendants, other parties, claims and notes. The <b>Lists</b> sheet has the accepted values.
        </p>
        <JisButton variant="outline" onClick={onTemplate}>
          Download template (.xlsx)
        </JisButton>
      </div>

      <div className="hctc-card">
        <div className="hctc-section-title">2. Upload the completed sheet</div>
        <div className="hctc-field" style={{ maxWidth: 420 }}>
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setRes(null);
            }}
          />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={() => send(true)} disabled={!file}>
            Validate
          </JisButton>
          {res?.validateOnly && res.ready > 0 && (
            <JisButton onClick={() => send(false)}>
              Import {res.ready} record{res.ready === 1 ? "" : "s"}
            </JisButton>
          )}
        </div>
      </div>

      {err && <div className="hctc-err">{err}</div>}

      {res && (
        <div className="hctc-card">
          <div className="head">
            <h3>{res.validateOnly ? "Validation result" : "Import result"}</h3>
          </div>
          <div className="hctc-stats">
            <div className="hctc-stat">
              <div className="n">{res.total}</div>
              <div className="l">Rows read</div>
            </div>
            <div className="hctc-stat">
              <div className="n">{res.validateOnly ? res.ready : res.imported}</div>
              <div className="l">{res.validateOnly ? "Ready to import" : "Imported"}</div>
            </div>
            <div className="hctc-stat">
              <div className="n">{res.duplicates}</div>
              <div className="l">Duplicates skipped</div>
            </div>
            <div className="hctc-stat">
              <div className="n">{res.errors}</div>
              <div className="l">Rows with errors</div>
            </div>
          </div>
          <div className="hctc-chips">
            {["all", res.validateOnly ? "Ready" : "Imported", "Duplicate", "Error"].map((k) => (
              <button key={k} className={`hctc-chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)}>
                {k === "all" ? "All" : k}
              </button>
            ))}
          </div>
          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table className="case-table hctc-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Case number</th>
                  <th>Title</th>
                  <th>Result</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row}>
                    <td>{r.row}</td>
                    <td>{r.id ? <a href={`/historical-cases/${r.id}`}>{r.number}</a> : r.number}</td>
                    <td className="wrap">{r.title}</td>
                    <td>
                      <span className={`hctc-pill ${tone(r.result)}`}>{r.result}</span>
                    </td>
                    <td className="wrap">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <BackLoader open={loading} />
    </div>
  );
}
